import type { NavigateFunction } from 'react-router-dom';
import { savePlatformReturnTarget } from '../../shared/session/returnTarget.js';
import { resolveShopHref } from '../shop/resolveShopHref.js';

export interface CustomerHubPaths {
  readonly shop: string;
  readonly publicShop: string;
  readonly findOrders: string;
  readonly donate: string;
  readonly orders: string;
  readonly account: string;
  readonly signIn: string;
}

/**
 * Wave D platform chrome SSOT — bare `/account/**`, `/sign-in`, `/shops`.
 * `tenantCode` retained for call-site compat; ignored for path construction.
 */
export function buildCustomerHubPaths(tenantCode: string): CustomerHubPaths {
  void tenantCode;
  return {
    shop: '/account/shop',
    publicShop: '/shops',
    findOrders: '/orders/find',
    donate: '/donate',
    orders: '/account/orders',
    account: '/account',
    signIn: '/sign-in',
  };
}

/**
 * Auth V2 — signed-in chrome when platform `/me` established session memory.
 */
export function isSignedInNavChrome(input: {
  session?: { readonly customerId: number } | null;
}): boolean {
  return input.session != null;
}

/**
 * Auth V2 — global signed-in chrome (same authority as URL-tenant nav).
 */
export function isGloballySignedInChrome(input: {
  session?: { readonly customerId: number } | null;
}): boolean {
  return input.session != null;
}

/**
 * L20 Shop entry CTA — session after `/me` → `/account/shop`, else favourites or `/shops`.
 */
export function navigateToShopEntry(input: {
  tenantCode: string;
  session?: { readonly customerId: number } | null;
  navigate: NavigateFunction;
  /** @deprecated Ignored for Shop CTA. */
  signedIn?: boolean;
  /** @deprecated Ignored for Shop CTA. */
  rememberedSalesPointSlug?: string | null;
  /** @deprecated Ignored for Shop CTA. */
  rememberedSalesPointId?: number | null;
  /** @deprecated Ignored for Shop CTA. */
  onNeedKioskHelp?: () => void;
}): void {
  void input.tenantCode;
  void input.signedIn;
  void input.rememberedSalesPointSlug;
  void input.rememberedSalesPointId;
  void input.onNeedKioskHelp;
  const href = resolveShopHref(undefined, input.session);
  input.navigate(href);
}

/**
 * L20 Shop entry CTA with optional return-target persistence (Wave D).
 */
export function navigateToShopEntryWithReturnTarget(input: {
  tenantCode: string;
  session?: { readonly customerId: number } | null;
  navigate: NavigateFunction;
}): void {
  void input.tenantCode;
  savePlatformReturnTarget(window.location.pathname + window.location.search);
  navigateToShopEntry(input);
}

